import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import { FirestoreDatabase } from '../index'
import { Collection } from 'constants/index'
import { signOut } from 'store/auth'
import { cloudFunctionsInstance } from 'config/axios'

export const fetchAllUsers = createAsyncThunk(
  'users/fetchAllUsers',
  async (conferenceId, { rejectWithValue }) => {
    try {
      const usersSnapshot = await FirestoreDatabase.collection(Collection.USERS)
        .where('role', '==', 'admin')
        .where('conferenceId', '==', conferenceId)
        .get()
      const users = usersSnapshot.docs.map((doc) => {
        return { uid: doc.id, ...doc.data() }
      })
      return users
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const createUser = async (payload) => {
  try {
    const response = await cloudFunctionsInstance.post('/createConferenceAdmin', payload)
    toast.success('Conference admin created successfully.')
    if (response.data.isEmailSent) {
      toast.success('An email has been sent to conference admin to confirm the account.')
    } else {
      toast.error('Failed to send welcome email.')
    }
  } catch (err) {
    const errorMsg =
      err.response?.data?.msg ||
      'Something went wrong when creating conference admin, please try again.'
    toast.error(errorMsg)
  }
}

export const fetchUserByUserId = createAsyncThunk(
  'users/fetchUserByUserId',
  async (uid, { rejectWithValue }) => {
    try {
      const userDoc = await FirestoreDatabase.collection(Collection.USERS).doc(uid).get()
      return { uid: userDoc.id, ...userDoc.data() }
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const deleteUserByUserId = createAsyncThunk(
  'users/deleteUserByUserId',
  async (uid, { rejectWithValue }) => {
    try {
      return await cloudFunctionsInstance.delete(`/deleteConferenceAdmin?uid=${uid}`)
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const updateUserPasswordByUserId = createAsyncThunk(
  'users/updateUserPasswordByUserId',
  async (requestData, { rejectWithValue }) => {
    try {
      return await cloudFunctionsInstance.post('/updateConferenceAdmin', {
        ...requestData
      })
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const initialState = {
  error: null,
  inProgress: false,
  admins: [],
  admin: {},
  confFilter: '',
  isCreatedOrUpdated: false,
  currentPage: 0,
  pageSize: 10
}

const adminsSlice = createSlice({
  name: 'attendee',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setAdmins: (state, action) => {
      state.admins = action.payload
    },
    setConfFilter: (state, action) => {
      state.confFilter = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setIsCreatedOrUpdated: (state, action) => {
      state.isCreatedOrUpdated = action.payload
    }
  },
  extraReducers: {
    [fetchAllUsers.pending]: (state) => {
      state.inProgress = true
    },
    [fetchAllUsers.fulfilled]: (state, action) => {
      state.inProgress = false
      state.admins = action.payload
      state.error = null
    },
    [fetchAllUsers.rejected]: (state, action) => {
      state.inProgress = false
      state.error = action.payload
    },
    [fetchUserByUserId.pending]: (state) => {
      state.inProgress = true
    },
    [fetchUserByUserId.fulfilled]: (state, action) => {
      state.inProgress = false
      state.admin = action.payload
      state.error = null
    },
    [fetchUserByUserId.rejected]: (state, action) => {
      state.inProgress = false
      state.error = action.payload
    },

    [deleteUserByUserId.pending]: (state) => {
      state.inProgress = true
    },
    [deleteUserByUserId.fulfilled]: (state, action) => {
      state.inProgress = false
      const { uid, msg } = action.payload.data
      state.admins = state.admins.filter((row) => row.uid !== uid)
      state.error = null
      toast.success(msg)
    },
    [deleteUserByUserId.rejected]: (state, action) => {
      state.inProgress = false
      state.error = action.payload
    },

    [updateUserPasswordByUserId.fulfilled]: (state, action) => {
      const { msg } = action.payload.data
      state.error = null
      toast.success(msg)
    },
    [updateUserPasswordByUserId.rejected]: (state, action) => {
      state.error = action.payload
    },
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.admins = []
      state.admin = {}
      state.confFilter = ''
      state.isCreatedOrUpdated = false
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export default adminsSlice
